'use client';

import { mergeClasses } from '@/helpers/style';
import { Input as NextUIInput, InputProps } from '@heroui/input';
import { Tooltip } from '@heroui/tooltip';
import clsx from 'clsx';
import React from 'react';

interface CustomInputProps extends Omit<InputProps, 'variant'> {
  variant?: 'outline';
  messageVariant?: 'default' | 'warning' | 'danger' | 'success';
  message?: string;
  tooltip?: string;
}

const Input = ({
  variant,
  messageVariant = 'default',
  message,
  tooltip,
  ...props
}: CustomInputProps) => {
  return tooltip ? (
    <Tooltip content={tooltip} placement="bottom" className="rounded-sm">
      <div className="h-full flex items-center justify-center">
        <>
          <NextUIInput
            {...props}
            classNames={{
              ...mergeClasses([
                {
                  ...(variant === 'outline' && {
                    inputWrapper: clsx(
                      'border border-gray-light2 h-12 bg-transparent data-[hover=true]:!bg-transparent data-[focus=true]:!bg-transparent group-hover:opacity-80 shadow-none transition-opacity',
                      {
                        'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-0 !ring-transparent data-[focus=true]:!border-[2px] data-[focus=true]:!border-primary-light-blue':
                          messageVariant === 'default',
                        '!border-green !border-[2px]':
                          messageVariant === 'success',
                        '!border-yellow !border-[2px]':
                          messageVariant === 'warning',
                        '!border-red-light !border-[2px]':
                          messageVariant === 'danger',
                      }
                    ),
                  }),
                },
                props.classNames as Record<string, string>,
              ]),
            }}
          />
          {Array.isArray(message) ? (
            <span className="px-[17px] pt-1 text-[13px] font-normal text-start flex items-start w-full justify-start whitespace-pre-wrap">
              {message.map((msg) => `- ${msg}`).join('\n')}
            </span>
          ) : (
            <span
              className={clsx(
                'px-[17px] pt-1 text-[13px] font-normal text-start flex items-start w-full justify-start whitespace-pre-wrap',
                {
                  hidden: messageVariant === 'default',
                  'text-green': messageVariant === 'success',
                  'text-yellow': messageVariant === 'warning',
                  'text-red-light': messageVariant === 'danger',
                }
              )}
            >
              {message}
            </span>
          )}
        </>
      </div>
    </Tooltip>
  ) : (
    <>
      <NextUIInput
        {...props}
        classNames={{
          ...mergeClasses([
            {
              ...(variant === 'outline' && {
                inputWrapper: clsx(
                  'border border-gray-light2 h-12 bg-transparent data-[hover=true]:!bg-transparent data-[focus=true]:!bg-transparent group-hover:opacity-80 shadow-none transition-opacity',
                  {
                    'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-0 !ring-transparent data-[focus=true]:!border-[2px] data-[focus=true]:!border-primary-light-blue':
                      messageVariant === 'default',
                    '!border-green !border-[2px]': messageVariant === 'success',
                    '!border-yellow !border-[2px]':
                      messageVariant === 'warning',
                    '!border-red-light !border-[2px]':
                      messageVariant === 'danger',
                  }
                ),
              }),
            },
            props.classNames as Record<string, string>,
          ]),
        }}
      />
      {Array.isArray(message) ? (
        <span className="px-[17px] pt-1 text-[13px] font-normal text-start flex items-start w-full justify-start whitespace-pre-wrap">
          {message.map((msg) => `- ${msg}`).join('\n')}
        </span>
      ) : (
        <span
          className={clsx(
            'px-[17px] pt-1 text-[13px] font-normal text-start flex items-start w-full justify-start whitespace-pre-wrap',
            {
              hidden: messageVariant === 'default',
              'text-green': messageVariant === 'success',
              'text-yellow': messageVariant === 'warning',
              'text-red-light': messageVariant === 'danger',
            }
          )}
        >
          {message}
        </span>
      )}
    </>
  );
};

Input.displayName = 'Input';
export default Input;
